import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { alertActions } from "_store";
import { ModalPopup } from "_components";
import { useCheckRole } from "_utils/role-access";
import { Typography, Button, Box } from "@mui/material";
import Popper from "@mui/material/Popper";
import Grid from "@material-ui/core/Grid";
import { AddCircleOutline } from "@mui/icons-material";
import { ERR_MSG } from "constants/labels";
import { PIPELINE_FIELD_MAPPING, createPipelineSchema } from "../../utils";
import PipelineFormFields from "../PipelineFormFields";

const fm = PIPELINE_FIELD_MAPPING;

const DEFAULT_VALUES = {
  [fm.name]: "",
  [fm.description]: "",
  [fm.isActive]: true,
  [fm.isConsumptionVisible]: true,
  [fm.isStorageVisible]: true,
};

const PipelineCreate = ({
  pipelines,
  handleRefresh,
  createApi,
  header = "Manage Marketer Pipelines",
  title = "Create Pipeline",
  buttonLabel = "Pipeline",
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasViewPipelineDeliveryMatrixAccess = useCheckRole(4, "ViewPipelineDeliveryMatrix");

  const [showMatrixPrompt, setShowMatrixPrompt] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDisable, setIsDisable] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(!isOpen);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const canBeOpen = isOpen && Boolean(anchorEl);
  const id = canBeOpen ? "pipeline-create-popper" : undefined;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(createPipelineSchema()),
    mode: "onBlur",
    defaultValues: DEFAULT_VALUES,
  });

  const watchedFields = watch();
  const prevFormValues = useRef(watchedFields);

  useEffect(() => {
    const hasChanged = Object.keys(watchedFields).some((key) => {
      return watchedFields[key] !== prevFormValues.current[key];
    });

    if (hasChanged && isDisable) {
      setIsDisable(false);
    }

    prevFormValues.current = watchedFields;
  }, [watchedFields, isDisable]);

  const onSubmit = async (data) => {
    setIsDisable(true);
    try {
      dispatch(alertActions.clear());

      // Check for duplicate names
      const isDuplicate = (pipelines || []).some(
        (item) =>
          item[fm.name]?.toLowerCase() === data[fm.name].trim().toLowerCase()
      );

      if (isDuplicate) {
        dispatch(
          alertActions.error({
            message: `${fm.nameLabel} already exists.`,
            header,
          })
        );
        setIsDisable(false);
        return;
      }

      const transformedData = {
        Data: {
          [fm.name]: data[fm.name].trim(),
          [fm.description]: data[fm.description].trim(),
          [fm.isActive]: !!data[fm.isActive],
          [fm.isConsumptionVisible]: !!data[fm.isConsumptionVisible],
          [fm.isStorageVisible]: !!data[fm.isStorageVisible],
        },
      };

      const result = await dispatch(createApi(transformedData)).unwrap();

      if (!result || !result?.Succeeded) {
        dispatch(
          alertActions.error({
            message: result?.Message || ERR_MSG["073"],
            header,
          })
        );
        setIsDisable(false);
        return;
      }

      dispatch(
        alertActions.success({
          message: result?.Message || "Created successfully",
          header,
          showAfterRedirect: true,
        })
      );

      if (hasViewPipelineDeliveryMatrixAccess) setShowMatrixPrompt(true);

      setTimeout(() => {
        handleRefresh();
      }, 1000);
    } catch (error) {
      setIsDisable(false);
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header,
        })
      );
    } finally {
      reset(DEFAULT_VALUES);
      onClose();
    }
  };

  const handleGoToEditMatrix = () => {
    setShowMatrixPrompt(false);
    navigate("/pipelinedelivery", { state: { openMatrix: true } });
  };

  const handleCancelClick = () => {
    reset(DEFAULT_VALUES);
    onClose();
  };

  return (
    <>
      <Button
        variant="contained"
        className="Download"
        color="primary"
        aria-describedby={id}
        onClick={handleClick}
      >
        <AddCircleOutline /> {buttonLabel}
      </Button>

      <Popper
        id={id}
        open={canBeOpen}
        anchorEl={anchorEl}
        className="Filtercontainer"
      >
        <Box
          sx={{ border: 1, p: 1, bgcolor: "background.paper" }}
          className="Filtercontainerinner CreateMarketerhight Border"
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="Registrationcontainer marketerFiltercontainer"
          >
            <Typography component="div" className="userprofilelist">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h2" className="userprofilelistcontent">
                    {title}
                  </Typography>
                </Grid>
              </Grid>
            </Typography>

            <PipelineFormFields
              control={control}
              errors={errors}
              trigger={trigger}
            />

            <Typography component="div" className="CreateMarketerbutton">
              <Button
                type="submit"
                variant="contained"
                className="submitbutton"
                color="primary"
                disabled={!isValid || isDisable}
              >
                Create
              </Button>
              <Button
                variant="contained"
                color="red"
                className="cancelbutton"
                onClick={handleCancelClick}
              >
                Cancel
              </Button>
            </Typography>
          </form>
        </Box>
      </Popper>

      {showMatrixPrompt && (
        <ModalPopup
          header={title}
          message1="Pipeline created successfully. Do you want to go to the Edit Matrix page to configure its delivery ranges?"
          btnPrimaryText="Yes"
          btnSecondaryText="No"
          handlePrimaryClick={handleGoToEditMatrix}
          handleSecondaryClick={() => setShowMatrixPrompt(false)}
        />
      )}
    </>
  );
};

export default PipelineCreate;
